import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import './Launch.css';

const META_KEY = 'launch_checkout_meta';
const CONTINUE_META_KEY = 'launch_continue_meta';
const CREATIVE_TOKEN_KEY = 'launch_creative_token';
const LEGACY_IMAGE_KEY = 'launch_image';

export default function LaunchSuccess() {
    const [searchParams] = useSearchParams();
    const sessionId = searchParams.get('session_id');
    const screenId = searchParams.get('screenId');
    const isContinueFlow =
        searchParams.get('continue') === '1'
        || (() => {
            try {
                const raw = sessionStorage.getItem(CONTINUE_META_KEY);
                return raw ? Boolean(JSON.parse(raw).continue) : false;
            } catch {
                return false;
            }
        })();

    const [phase, setPhase] = useState('working'); // working | done | error
    const [errorMessage, setErrorMessage] = useState('');
    const [campaignName, setCampaignName] = useState('');
    const [isNewAdvertiser, setIsNewAdvertiser] = useState(false);
    const [temporaryPassword, setTemporaryPassword] = useState(null);

    useEffect(() => {
        let cancelled = false;

        const run = async () => {
            if (!sessionId) {
                setPhase('error');
                setErrorMessage('Missing payment session. Return to checkout from your launch link.');
                return;
            }

            const creativeToken = sessionStorage.getItem(CREATIVE_TOKEN_KEY);
            const legacyImage = sessionStorage.getItem(LEGACY_IMAGE_KEY);
            const hasCreative = Boolean(creativeToken) || (legacyImage && legacyImage.startsWith('data:'));

            let continueMeta = {};
            try {
                const rawContinue = sessionStorage.getItem(CONTINUE_META_KEY);
                if (rawContinue) continueMeta = JSON.parse(rawContinue);
            } catch {
                continueMeta = {};
            }

            if (!isContinueFlow && !hasCreative) {
                setPhase('error');
                setErrorMessage(
                    'We could not restore your creative after payment (this can happen if you opened this page in another browser or cleared storage). Contact support with your Stripe receipt; we can still match your payment.'
                );
                return;
            }

            let meta = {};
            try {
                const raw = sessionStorage.getItem(META_KEY);
                if (raw) meta = JSON.parse(raw);
            } catch {
                meta = {};
            }

            const displayCampaignName = continueMeta.campaignName || meta.campaignName;
            if (displayCampaignName) setCampaignName(displayCampaignName);
            if (meta.isNewAdvertiser) setIsNewAdvertiser(true);
            if (meta.temporaryPassword) setTemporaryPassword(meta.temporaryPassword);

            try {
                const finalizeBody = isContinueFlow
                    ? { sessionId }
                    : {
                        sessionId,
                        ...(creativeToken
                            ? { creativeToken }
                            : { image: legacyImage }),
                        isNewAdvertiser: Boolean(meta.isNewAdvertiser),
                        temporaryPassword: meta.temporaryPassword || null,
                    };

                const res = await fetch(`${process.env.REACT_APP_API_URL}/launch/checkout/finalize-stripe-payment`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(finalizeBody),
                });

                const data = await res.json().catch(() => ({}));
                if (!res.ok) {
                    throw new Error(data.error || 'Could not complete your launch. Please contact support.');
                }

                if (!cancelled) {
                    sessionStorage.removeItem(CREATIVE_TOKEN_KEY);
                    sessionStorage.removeItem(LEGACY_IMAGE_KEY);
                    sessionStorage.removeItem('launch_message');
                    sessionStorage.removeItem(META_KEY);
                    sessionStorage.removeItem(CONTINUE_META_KEY);
                    setPhase('done');
                }
            } catch (e) {
                if (!cancelled) {
                    setPhase('error');
                    setErrorMessage(e.message || 'Something went wrong.');
                }
            }
        };

        run();
        return () => {
            cancelled = true;
        };
    }, [sessionId, isContinueFlow]);

    const launchHref = screenId ? `/launch?screenId=${encodeURIComponent(screenId)}` : '/launch';

    if (phase === 'error') {
        return (
            <div className="launch-container">
                <div className="launch-header">
                    <div className="launch-logo">INVERT</div>
                </div>
                <div className="launch-content">
                    <div className="launch-error">
                        <h2>We could not finish setup</h2>
                        <p>{errorMessage}</p>
                        <Link className="launch-btn primary" style={{ display: 'inline-block', textDecoration: 'none', textAlign: 'center' }} to={launchHref}>
                            Back to launch
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    if (phase === 'working') {
        return (
            <div className="launch-container">
                <div className="launch-header">
                    <div className="launch-logo">INVERT</div>
                </div>
                <div className="launch-content">
                    <div className="launch-step">
                        <div className="launch-success-pulse" aria-hidden="true">
                            <div className="launch-success-ring" />
                        </div>
                        <h1>Confirming payment…</h1>
                        <h2>Hang tight while we save your campaign</h2>
                        <div className="launch-spinner">
                            <div className="spinner-circle">↑</div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="launch-container">
            <div className="launch-header">
                <div className="launch-logo">INVERT</div>
            </div>
            <div className="launch-content">
                <div className="launch-step launch-success-card">
                    <div className="launch-success-icon" aria-hidden="true">✓</div>
                    <h1>You are all set</h1>
                    <h2>Your campaign is in review</h2>
                    <p className="launch-success-lead">
                        Payment went through. We have your creative and our team will review it shortly. You will get a separate note when it is approved for the screen.
                    </p>
                    {campaignName && (
                        <p className="launch-success-detail">
                            <span className="launch-success-label">Campaign</span>
                            {campaignName}
                        </p>
                    )}
                    <p className="launch-success-inbox">
                        Check your inbox for your receipt and a short confirmation—we also notified our team so nothing gets missed.
                    </p>
                    {isNewAdvertiser && temporaryPassword && (
                        <div className="success-info">
                            <p>
                                A new advertiser account was created for you. Your temporary password was included in your receipt email—log in and change it as soon as you can.
                            </p>
                        </div>
                    )}
                    <button type="button" className="launch-btn primary" onClick={() => { window.location.href = '/'; }}>
                        Back to home
                    </button>
                </div>
            </div>
        </div>
    );
}
